import { ExternalLink, MoreVertical, Plus, Trash } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { IconButton } from "src/components/ui/icon-button";
import GitLabIcon from "src/components/ui/icons/gitlab";
import { ErrorAlert } from "src/components/ui/status-alert";
import { Switch } from "src/components/ui/switch";
import { Tooltip } from "src/components/ui/tooltip";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { GitLabIntegrationsDialogDeleteIntegrationMutation } from "./__generated__/GitLabIntegrationsDialogDeleteIntegrationMutation.graphql";
import type { GitLabIntegrationsDialogQuery } from "./__generated__/GitLabIntegrationsDialogQuery.graphql";
import type { GitLabIntegrationsDialogUpdateSmartSyncMutation } from "./__generated__/GitLabIntegrationsDialogUpdateSmartSyncMutation.graphql";
import GitLabIntegration from "./GitLabIntegration";

interface GitLabIntegrationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const gitLabIntegrationsDialogQuery = graphql`
  query GitLabIntegrationsDialogQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: GITLAB) {
          edges {
            node {
              id
              name
              integrationType
              integrationStatus
              credentials
              createdAt
              gitlabInstanceUrl
              gitlabUsername
              smartSyncSettings {
                days
                status
                backfillError
              }
            }
          }
        }
      }
    }
  }
`;

const updateSmartSyncMutation = graphql`
  mutation GitLabIntegrationsDialogUpdateSmartSyncMutation($input: UpdateSmartSyncInput!) {
    updateSmartSync(input: $input) {
      integration {
        id
        smartSyncSettings {
          days
          status
          backfillError
        }
      }
      error
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation GitLabIntegrationsDialogDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

export default function GitLabIntegrationsDialog({ isOpen, onClose, onRefetch, brandTheme }: GitLabIntegrationsDialogProps) {
  const [isConnectingNew, setIsConnectingNew] = useState(false);
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    integration: any | null;
  }>({
    isOpen: false,
    integration: null,
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();

  const [commitDeleteIntegration] = useMutation<GitLabIntegrationsDialogDeleteIntegrationMutation>(
    deleteIntegrationMutation
  );

  const [commitUpdateSmartSync] = useMutation<GitLabIntegrationsDialogUpdateSmartSyncMutation>(
    updateSmartSyncMutation
  );

  const data = isOpen
    ? useLazyLoadQuery<GitLabIntegrationsDialogQuery>(
        gitLabIntegrationsDialogQuery,
        {},
        { fetchPolicy: "network-only" },
      )
    : null;

  const gitlabIntegrations = data?.me.organization?.integrations?.edges || [];

  const handleConnectAnother = () => {
    setIsConnectingNew(true);
  };

  const handleDeleteClick = (integration: any) => {
    setDeleteConfirmDialog({
      isOpen: true,
      integration,
    });
    setDeleteError(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmDialog.integration) {
      setDeleteError("Integration not found");
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    commitDeleteIntegration({
      variables: {
        input: {
          id: deleteConfirmDialog.integration.id,
        },
      },
      onCompleted: (response, errors) => {
        setIsDeleting(false);

        if (errors && errors.length > 0) {
          setDeleteError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setDeleteError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setDeleteConfirmDialog({ isOpen: false, integration: null });
          toast({
            title: "GitLab integration disconnected",
            variant: "success",
          });

          if (gitlabIntegrations.length <= 1) {
            onClose();
          }

          onRefetch?.();
        }
      },
      onError: (error) => {
        setIsDeleting(false);
        setDeleteError(error.message);
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ isOpen: false, integration: null });
    setDeleteError(null);
  };

  const handleSmartSyncToggle = (integrationId: string, currentlyEnabled: boolean) => {
    commitUpdateSmartSync({
      variables: {
        input: {
          integrationId,
          enabled: !currentlyEnabled,
        },
      },
      onCompleted: (response) => {
        if (response.updateSmartSync.error) {
          toast({
            title: "Failed to update Efficient Sync",
            description: response.updateSmartSync.error,
            variant: "error",
          });
          return;
        }
        toast({
          title: !currentlyEnabled ? "Efficient Sync enabled" : "Efficient Sync disabled",
          variant: "success",
        });
        onRefetch?.();
      },
      onError: (error) => {
        toast({
          title: "Failed to update Efficient Sync",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  // If connecting new, show the connection flow
  if (isConnectingNew) {
    return (
      <GitLabIntegration
        isOpen={isOpen}
        onClose={() => {
          setIsConnectingNew(false);
          onClose();
        }}
        brandTheme={brandTheme}
      />
    );
  }

  // If no integrations, show the full GitLab connection flow
  if (gitlabIntegrations.length === 0) {
    return <GitLabIntegration isOpen={isOpen} onClose={onClose} brandTheme={brandTheme} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-4xl bg-popover text-foreground border-border"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <GitLabIcon className="h-6 w-6" />
            GitLab Integrations
          </DialogTitle>
          <DialogDescription>
            Manage your GitLab integrations. You can connect multiple GitLab instances.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Connected Integrations</h3>

            {/* Header row */}
            <div className="grid grid-cols-[minmax(200px,2fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_40px] gap-4 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground">Instance</div>
              <div className="text-sm font-medium text-muted-foreground">Username</div>
              <div className="text-sm font-medium text-muted-foreground">Connected</div>
              <div className="text-sm font-medium text-muted-foreground">Efficient Sync</div>
              <div></div>
            </div>

            {/* Integration rows */}
            {gitlabIntegrations.map((edge): React.ReactNode => {
              const integration = edge.node;
              const instanceUrl = integration.gitlabInstanceUrl || "Unknown";
              const username = integration.gitlabUsername || "Unknown";
              const createdAt = integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : "N/A";
              const displayUrl = instanceUrl.replace("https://", "");
              const smartSyncEnabled = !!integration.smartSyncSettings;

              return (
                <div key={integration.id} className="grid grid-cols-[minmax(200px,2fr)_minmax(120px,1fr)_minmax(100px,1fr)_minmax(100px,1fr)_40px] gap-4 items-start py-2">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-foreground font-medium truncate">{displayUrl}</span>
                  </div>
                  <div className="text-muted-foreground text-sm pt-0.5 min-w-0 truncate">{username}</div>
                  <div className="text-muted-foreground text-sm pt-0.5 min-w-0">{createdAt}</div>
                  <div className="pt-0.5">
                    <Tooltip
                      content="Only give the agent access to recently used repos"
                      position="top"
                    >
                      <Switch
                        checked={smartSyncEnabled}
                        onCheckedChange={() => handleSmartSyncToggle(integration.id, smartSyncEnabled)}
                        disabled={!canModify}
                      />
                    </Tooltip>
                  </div>
                  <div className="flex gap-2 items-start pt-0.5 justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton
                          icon={MoreVertical}
                          variant="none"
                          size="sm"
                          className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                          title="More options"
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-card border-border text-foreground">
                        <DropdownMenuItem
                          onClick={() => {
                            window.open(instanceUrl, "_blank");
                          }}
                          className="hover:bg-accent cursor-pointer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View on GitLab
                        </DropdownMenuItem>
                        <div>
                          <Tooltip
                            content={!canModify ? "You don't have permission to disconnect integrations" : null}
                            position="left"
                          >
                            <DropdownMenuItem
                              onClick={() => canModify && handleDeleteClick(integration)}
                              className="hover:bg-accent cursor-pointer text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!canModify}
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Disconnect
                            </DropdownMenuItem>
                          </Tooltip>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}

            {/* Action buttons */}
            <div className="pt-4 border-t border-accent">
              <p className="text-sm text-muted-foreground mb-3">
                Connect {productName} to additional GitLab instances for repository access and analysis.
              </p>
              <Tooltip
                content={!canModify ? "You don't have permission to connect integrations" : null}
                position="top"
              >
                <span className="inline-block">
                  <Button
                    onClick={handleConnectAnother}
                    disabled={!canModify}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Connect Another GitLab
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Delete confirmation dialog */}
      <UIConfirmationDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Disconnect GitLab Integration"
        confirmText={isDeleting ? "Disconnecting..." : "Disconnect"}
        cancelText="Cancel"
        variant="destructive"
        confirmDisabled={isDeleting}
      >
        <div className="space-y-3">
          <p>
            Are you sure you want to disconnect the GitLab integration for{" "}
            <span className="font-semibold">
              {deleteConfirmDialog.integration?.gitlabInstanceUrl?.replace("https://", "") || "this instance"}
            </span>
            ?
          </p>
          <p className="text-sm text-muted-foreground">
            This will remove access to all projects from this integration. This action cannot be undone.
          </p>

          {deleteError && (
            <ErrorAlert icon="none">
              {deleteError}
            </ErrorAlert>
          )}
        </div>
      </UIConfirmationDialog>
    </Dialog>
  );
}
